"use client"

import { useTr } from "../../../lib/use-tr"
import {
  Columns3,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  Loader2,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import type { LeadExportFormat } from "@/lib/apps/autoleads/lead-export"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { CAMPAIGN_ACTIONS_T } from "./translations"

// From the floating selection bar the menu opens above it, centred on the
// button. The bar pads its buttons by 6px, so this leaves 8px between the menu
// and the bar's edge; the padding keeps it off a phone's screen edge.
const BAR_MENU_SIDE_OFFSET = 14
const BAR_MENU_COLLISION_PADDING = 16

// Menu order: the raw record first, then the two spreadsheet formats.
const FORMATS: {
  format: LeadExportFormat
  labelKey: keyof typeof CAMPAIGN_ACTIONS_T & string
  Icon: LucideIcon
}[] = [
  { format: "json", labelKey: "downloadJson", Icon: FileJson },
  { format: "csv", labelKey: "downloadCsv", Icon: FileText },
  { format: "xlsx", labelKey: "downloadExcel", Icon: FileSpreadsheet },
]

type Props = {
  // What the trigger announces: one lead's card, or the whole campaign.
  label: string
  onSelect: (format: LeadExportFormat) => Promise<void>
  // Opens the team's export setup; absent where the reader cannot edit it.
  onConfigure?: () => void
  variant?: "ghost" | "outline"
  // Quiet the trigger where it sits beside a lead's own text.
  tone?: "muted"
  className?: string
  iconClassName?: string
  // Replaces the default icon button, e.g. to match a toolbar's own buttons.
  renderTrigger?: (state: { pending: boolean }) => React.ReactElement
  disabled?: boolean
  // "bar": opened from the dark floating selection bar, whose surface it takes.
  surface?: "default" | "bar"
}

// The download control shared by the lead card and the campaign header: same
// formats, same order, same wording, only the trigger changes. The export
// setup (columns, contact layout) sits under the formats it shapes.
export function ExportFormatMenu({
  label,
  onSelect,
  onConfigure,
  variant = "ghost",
  tone,
  className,
  iconClassName = "size-4",
  renderTrigger,
  disabled = false,
  surface = "default",
}: Props) {
  const tr = useTr(CAMPAIGN_ACTIONS_T)
  const [pending, setPending] = useState(false)

  const run = async (format: LeadExportFormat) => {
    setPending(true)
    try {
      await onSelect(format)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : tr("downloadError"))
    } finally {
      setPending(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled || pending}>
        {renderTrigger ? (
          renderTrigger({ pending })
        ) : (
          <Button
            type="button"
            variant={variant}
            tone={tone}
            size="icon"
            className={className}
            aria-label={label}
            title={label}
            disabled={pending}
          >
            {pending ? (
              <Loader2 className={`${iconClassName} animate-spin`} />
            ) : (
              <Download className={iconClassName} />
            )}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-40"
        {...(surface === "bar"
          ? {
              variant: "inverse",
              side: "top",
              align: "center",
              sideOffset: BAR_MENU_SIDE_OFFSET,
              collisionPadding: BAR_MENU_COLLISION_PADDING,
            }
          : { align: "end" })}
      >
        <DropdownMenuLabel tone="muted" className="text-xs font-normal">
          {tr("downloadMenuLabel")}
        </DropdownMenuLabel>
        {FORMATS.map(({ format, labelKey, Icon }) => (
          <DropdownMenuItem
            key={format}
            onSelect={() => {
              void run(format)
            }}
          >
            <Icon className="text-muted-foreground size-4" />
            {tr(labelKey)}
          </DropdownMenuItem>
        ))}
        {onConfigure ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onConfigure}>
              <Columns3 className="text-muted-foreground size-4" />
              {tr("downloadSettings")}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
